import {
  approveWorkspaceTeamMemberMutation,
  createInviteLinkMutation,
  getMembersByWorkspaceIdQuery,
  grantWorkspaceTeamMemberMutation,
  inviteByEmailsMutation,
  type Permission,
  revokeInviteLinkMutation,
  revokeMemberPermissionMutation,
  transferWorkspaceOwnershipMutation,
  type WorkspaceInviteLinkExpireTime,
} from '@affine/graphql';
import { Store } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import {
  memberToCachedWorkspacePrincipal,
  upsertCachedWorkspacePrincipals,
  workspacePrincipalCacheScopeFromServer,
} from '../utils/workspace-principal-cache';

export class WorkspaceMembersStore extends Store {
  constructor(private readonly workspaceServerService: WorkspaceServerService) {
    super();
  }

  async fetchMembers(
    workspaceId: string,
    skip: number,
    take: number,
    searchQuery?: string,
    signal?: AbortSignal
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    const data = await this.workspaceServerService.server.gql({
      query: getMembersByWorkspaceIdQuery,
      variables: {
        workspaceId,
        skip,
        take,
        query: searchQuery || undefined,
      },
      context: {
        signal,
      },
    });

    const workspace = data.workspace;
    upsertCachedWorkspacePrincipals(
      workspacePrincipalCacheScopeFromServer(
        workspaceId,
        this.workspaceServerService.server
      ),
      (workspace.members ?? []).map(member =>
        memberToCachedWorkspacePrincipal(workspaceId, member)
      )
    );

    return workspace;
  }

  async inviteBatch(
    workspaceId: string,
    emails: string[],
    options?: {
      permission?: Permission;
    }
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    const inviteBatch = await this.workspaceServerService.server.gql({
      query: inviteByEmailsMutation,
      variables: {
        workspaceId,
        emails,
        permission: options?.permission,
      },
    });
    return inviteBatch.inviteMembers;
  }

  async generateInviteLink(
    workspaceId: string,
    expireTime: WorkspaceInviteLinkExpireTime
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    const inviteLink = await this.workspaceServerService.server.gql({
      query: createInviteLinkMutation,
      variables: {
        workspaceId,
        expireTime,
      },
    });
    return inviteLink.createInviteLink;
  }

  async revokeInviteLink(workspaceId: string, signal?: AbortSignal) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    const revoke = await this.workspaceServerService.server.gql({
      query: revokeInviteLinkMutation,
      variables: {
        workspaceId,
      },
      context: { signal },
    });
    return revoke.revokeInviteLink;
  }

  async revokeMemberPermission(
    workspaceId: string,
    userId: string,
    signal?: AbortSignal
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    const revoke = await this.workspaceServerService.server.gql({
      query: revokeMemberPermissionMutation,
      variables: {
        workspaceId,
        userId,
      },
      context: { signal },
    });
    return revoke.revokeMember;
  }

  async approveMember(workspaceId: string, userId: string) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    const member = await this.workspaceServerService.server.gql({
      query: approveWorkspaceTeamMemberMutation,
      variables: {
        workspaceId,
        userId,
      },
    });
    return member.approveMember;
  }

  async adjustMemberPermission(
    workspaceId: string,
    userId: string,
    permission: Permission
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    const member = await this.workspaceServerService.server.gql({
      query: grantWorkspaceTeamMemberMutation,
      variables: {
        workspaceId,
        userId,
        permission,
      },
    });
    return member.grantMember;
  }

  async transferWorkspaceOwnership(workspaceId: string, userId: string) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    const result = await this.workspaceServerService.server.gql({
      query: transferWorkspaceOwnershipMutation,
      variables: {
        workspaceId,
        userId,
      },
    });
    return result.transferWorkspaceOwnership;
  }
}
